const ACCESS_TOKEN_KEYS = ['accessToken', 'token', 'jwt'];
const USER_KEY = 'user';
const REFRESH_TOKEN_KEYS = ['refreshToken', 'refresh_token'];
const ACCESS_TOKEN_EXPIRES_AT_KEY = 'auth.accessTokenExpiresAt';
const REFRESH_TOKEN_EXPIRES_AT_KEY = 'auth.refreshTokenExpiresAt';
const REMEMBER_ME_KEY = 'auth.rememberMe';

let refreshPromise = null;
let refreshTimerId = null;

function getStorageList() {
  const storages = [];

  if (typeof window === 'undefined') {
    return storages;
  }

  try {
    if (window.localStorage) {
      storages.push(window.localStorage);
    }
  } catch (error) {
    // ignore storage access errors
  }

  try {
    if (window.sessionStorage) {
      storages.push(window.sessionStorage);
    }
  } catch (error) {
    // ignore storage access errors
  }

  return storages;
}

function findStoredValue(keys) {
  const keyList = Array.isArray(keys) ? keys : [keys];

  for (const storage of getStorageList()) {
    for (const key of keyList) {
      const value = storage.getItem(key);
      if (value) {
        return { value, storage, key };
      }
    }
  }

  return null;
}

function removeStoredKeys(keys) {
  const keyList = Array.isArray(keys) ? keys : [keys];

  for (const storage of getStorageList()) {
    for (const key of keyList) {
      storage.removeItem(key);
    }
  }
}

function clearRefreshTimer() {
  if (refreshTimerId !== null && typeof window !== 'undefined') {
    window.clearTimeout(refreshTimerId);
  }

  refreshTimerId = null;
}

function getPreferredSessionStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
}

function getPreferredPersistentStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
}

function getStoredTimestamp(key) {
  const stored = findStoredValue(key)?.value;
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function storeJson(storage, key, value) {
  if (!storage) return;
  storage.setItem(key, JSON.stringify(value));
}

function storeString(storage, key, value) {
  if (!storage || value === undefined || value === null || value === '') return;
  storage.setItem(key, String(value));
}

function hasUsableRefreshToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  const expiresAt = getStoredTimestamp(REFRESH_TOKEN_EXPIRES_AT_KEY);
  return !expiresAt || expiresAt > Date.now();
}

function scheduleAccessTokenRefresh() {
  clearRefreshTimer();

  if (!hasUsableRefreshToken() || typeof window === 'undefined') {
    return;
  }

  const payload = decodeJwtPayload(getAccessToken());
  if (!payload || typeof payload.exp !== 'number') {
    return;
  }

  const refreshInMs = Math.max((payload.exp * 1000) - Date.now() - 60 * 1000, 0);
  refreshTimerId = window.setTimeout(() => {
    refreshAccessToken().catch(() => {
      clearStoredAuth();
    });
  }, refreshInMs);
}

export function getAccessToken() {
  return findStoredValue(ACCESS_TOKEN_KEYS)?.value || '';
}

export function getApiBaseUrl() {
  return (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
}

export function getRefreshToken() {
  return findStoredValue(REFRESH_TOKEN_KEYS)?.value || '';
}

export function isAdminFlag(value) {
  if (value === true || value === 1) return true;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }

  return false;
}

export function getStoredUser() {
  try {
    const rawUser = findStoredValue(USER_KEY)?.value;
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    return null;
  }
}

export function setStoredUser(user) {
  if (!user || typeof user !== 'object') {
    removeStoredKeys(USER_KEY);
    return;
  }

  const existing = findStoredValue(USER_KEY);
  const rememberMe = findStoredValue(REMEMBER_ME_KEY)?.value === '1';
  const targetStorage = existing?.storage || (rememberMe ? getPreferredPersistentStorage() : getPreferredSessionStorage()) || getPreferredPersistentStorage();

  removeStoredKeys(USER_KEY);
  storeJson(targetStorage, USER_KEY, user);
}

export function hasAdminAccess(user, payload) {
  return Boolean(
    isAdminFlag(user?.is_admin)
      || isAdminFlag(payload?.is_admin)
      || user?.admin_role
      || payload?.admin_role
  );
}

export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;

  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
}

export function isTokenExpired(payload) {
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 <= Date.now();
}

export function getAuthState() {
  const token = getAccessToken();
  const payload = decodeJwtPayload(token);
  const storedUser = getStoredUser();
  const authenticated = Boolean(token && payload && !isTokenExpired(payload) && payload.id && payload.email);

  return {
    token,
    payload,
    authenticated,
    isAdmin: Boolean(authenticated && hasAdminAccess(storedUser, payload)),
    adminRole: authenticated && payload ? (payload.admin_role ?? null) : null
  };
}

export function clearStoredAuth() {
  clearRefreshTimer();
  refreshPromise = null;
  removeStoredKeys(ACCESS_TOKEN_KEYS);
  removeStoredKeys(USER_KEY);
  removeStoredKeys(REFRESH_TOKEN_KEYS);
  removeStoredKeys(ACCESS_TOKEN_EXPIRES_AT_KEY);
  removeStoredKeys(REFRESH_TOKEN_EXPIRES_AT_KEY);
  removeStoredKeys(REMEMBER_ME_KEY);
}

export function saveAuthSession({ token, refreshToken, expiresIn, refreshExpiresAt, user, rememberMe = false }) {
  const accessToken = token || '';
  if (!accessToken) {
    clearStoredAuth();
    return;
  }

  clearStoredAuth();

  const targetStorage = rememberMe ? getPreferredPersistentStorage() : getPreferredSessionStorage();
  const fallbackStorage = targetStorage || getPreferredPersistentStorage();
  const payload = decodeJwtPayload(accessToken);
  const accessExpiresAt = payload?.exp
    ? payload.exp * 1000
    : Date.now() + ((Number(expiresIn) || 60 * 60) * 1000);

  storeString(fallbackStorage, ACCESS_TOKEN_KEYS[0], accessToken);
  storeString(fallbackStorage, ACCESS_TOKEN_EXPIRES_AT_KEY, accessExpiresAt);
  storeString(fallbackStorage, REMEMBER_ME_KEY, rememberMe ? '1' : '0');

  if (user) {
    storeJson(fallbackStorage, USER_KEY, user);
  }

  if (rememberMe && refreshToken) {
    const persistentStorage = getPreferredPersistentStorage() || fallbackStorage;
    const refreshExpiresAtValue = refreshExpiresAt ? new Date(refreshExpiresAt).getTime() : null;
    storeString(persistentStorage, REFRESH_TOKEN_KEYS[0], refreshToken);
    if (refreshExpiresAtValue) {
      storeString(persistentStorage, REFRESH_TOKEN_EXPIRES_AT_KEY, refreshExpiresAtValue);
    }
  }

  scheduleAccessTokenRefresh();
}

export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken || !hasUsableRefreshToken()) {
    clearStoredAuth();
    return { ok: false, status: 401, data: { error: 'Sesion expirada' } };
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: storedRefreshToken })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          clearStoredAuth();
        }

        return { ok: false, status: response.status, data };
      }

      const nextAccessToken = data.token || data.accessToken || data.access_token || '';
      if (!nextAccessToken) {
        clearStoredAuth();
        return { ok: false, status: 500, data: { error: 'No se recibio access token' } };
      }

      saveAuthSession({
        token: nextAccessToken,
        refreshToken: data.refreshToken || data.refresh_token || storedRefreshToken,
        expiresIn: data.expires_in,
        refreshExpiresAt: data.refreshExpiresAt || data.refresh_expires_at,
        user: getStoredUser(),
        rememberMe: true
      });

      return { ok: true, status: response.status, data, token: nextAccessToken };
    } catch (error) {
      return { ok: false, status: 0, error, data: { error: 'No se pudo renovar la sesion' } };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function ensureValidSession() {
  const currentToken = getAccessToken();
  const currentPayload = decodeJwtPayload(currentToken);

  if (currentToken && currentPayload && !isTokenExpired(currentPayload)) {
    scheduleAccessTokenRefresh();
    return {
      ok: true,
      status: 200,
      token: currentToken,
      payload: currentPayload,
      user: getStoredUser()
    };
  }

  if (!hasUsableRefreshToken()) {
    clearStoredAuth();
    return { ok: false, status: 401, data: { error: 'Sesion expirada' } };
  }

  const refreshResult = await refreshAccessToken();
  if (!refreshResult.ok) {
    clearStoredAuth();
    return refreshResult;
  }

  const token = getAccessToken();
  const payload = decodeJwtPayload(token);
  return {
    ok: Boolean(token && payload && !isTokenExpired(payload)),
    status: refreshResult.status,
    token,
    payload,
    user: getStoredUser(),
    refreshed: true,
    data: refreshResult.data
  };
}

export async function logoutUser() {
  const token = getAccessToken();
  const refreshToken = getRefreshToken();

  try {
    if (refreshToken) {
      const response = await fetch(`${getApiBaseUrl()}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      const data = await response.json().catch(() => ({}));
      clearStoredAuth();
      return { ok: response.ok, status: response.status, data };
    }

    clearStoredAuth();
    return { ok: true, status: 200, data: { message: 'Logout local exitoso' } };
  } catch (error) {
    clearStoredAuth();
    return { ok: false, status: 0, error, data: { error: 'No se pudo cerrar la sesion en el servidor' } };
  }
}

export async function verifyAdminAccess(token = getAccessToken()) {
  if (!token) {
    return { ok: false, status: 401 };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/admin/verify`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      return { ok: false, status: response.status };
    }

    const data = await response.json().catch(() => ({}));
    return { ok: true, status: response.status, data };
  } catch (error) {
    return { ok: false, status: 0, error };
  }
}

export function resolveAdminAccess(authState, verificationResult) {
  if (!authState?.authenticated) {
    return 'unauthenticated';
  }

  if (verificationResult?.ok) {
    return 'authorized';
  }

  if (verificationResult?.status === 401) {
    return 'unauthenticated';
  }

  if ((verificationResult?.status === 0 || verificationResult?.status === 404 || verificationResult?.status >= 500) && authState?.isAdmin) {
    return 'authorized';
  }

  return 'forbidden';
}